import { useState } from "react";
import { Star, BadgeCheck, ChevronDown } from "lucide-react";
import { Reveal } from "@/components/motion";
import Modal from "@/components/Modal";

const Stars = ({ n = 5 }) => (
  <div className="flex gap-1" aria-hidden="true">
    {[...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${i < n ? "fill-terracotta text-terracotta" : "text-linen"}`}
      />
    ))}
  </div>
);

// Privacy policy, section 8: only the initials of a reviewer are published.
const initials = (name) => name.trim().split(/\s+/).filter(Boolean).map((w) => `${w[0].toLocaleUpperCase()}.`).join(" ");

const ReviewCard = ({ rev, up, lang, testId }) => (
  <figure className="rounded-2xl border border-linen bg-card p-7" data-testid={testId}>
    <div className="flex items-center justify-between flex-wrap gap-2">
      <figcaption className="font-display italic text-xl">{initials(rev.name)}</figcaption>
      <Stars n={rev.rating} />
    </div>
    <blockquote className="mt-3.5 text-ink/85 font-light leading-relaxed">
      “{rev.text[lang] ?? rev.text.en}”
    </blockquote>
    <p className="mt-4 text-xs uppercase tracking-[0.15em] text-stone flex items-center gap-1.5">
      <BadgeCheck className="h-3.5 w-3.5 text-olive" />
      {rev.date[lang] ?? rev.date.en} · {up.verified}
    </p>
  </figure>
);

const CATEGORIES = ["cleanliness", "accuracy", "checkin", "communication", "location", "value"];
const PREVIEW = 3;

const UnitReviews = ({ unit, up, lang }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const preview = unit.reviews.slice(0, PREVIEW);

  return (
    <section id="recensioni" className="max-w-7xl mx-auto px-6 md:px-12 mt-24 md:mt-32 scroll-mt-28" data-testid="unit-reviews">
      <Reveal>
        <h2 className="font-display text-3xl md:text-5xl tracking-tight">{up.reviewsTitle}</h2>
      </Reveal>
      <div className="grid lg:grid-cols-12 gap-12 mt-10">
        <Reveal className="lg:col-span-4">
          <div className="rounded-2xl border border-linen bg-card p-8 lg:sticky lg:top-28" data-testid="unit-rating-summary">
            <p className="font-display text-6xl text-terracotta">{unit.rating}</p>
            <div className="mt-3"><Stars n={5} /></div>
            <p className="mt-3 text-sm text-stone">
              {unit.reviewsCount} {up.reviews} · {up.verified}
            </p>
            <div className="mt-7 space-y-3.5">
              {CATEGORIES.map((c) => (
                <div key={c} className="flex items-center gap-3" data-testid={`rating-bar-${c}`}>
                  <span className="text-xs text-stone w-28 shrink-0">{up.categories[c]}</span>
                  <div className="flex-1 h-1.5 rounded-full bg-sand overflow-hidden">
                    <div
                      className="h-full bg-terracotta rounded-full"
                      style={{ width: `${(unit.ratingBreakdown[c] / 5) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs font-semibold w-8 text-right">{unit.ratingBreakdown[c].toFixed(1)}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="lg:col-span-8 space-y-5">
          {preview.map((rev, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <ReviewCard rev={rev} up={up} lang={lang} testId={`unit-review-${i}`} />
            </Reveal>
          ))}
          {unit.reviews.length > PREVIEW && (
            <div className="flex justify-center pt-3">
              <button
                type="button"
                data-testid="unit-reviews-toggle"
                onClick={() => setModalOpen(true)}
                className="group inline-flex items-center gap-3 rounded-full border border-ink/20 px-8 py-4 text-sm font-medium hover:border-terracotta hover:text-terracotta transition-colors duration-200 btn-press"
              >
                {`${up.showMore} · ${unit.reviews.length}`}
                <ChevronDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
              </button>
            </div>
          )}
        </div>
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={up.reviewsTitle} testId="unit-reviews-modal">
        <div className="space-y-5">
          {unit.reviews.map((rev, i) => (
            <ReviewCard key={i} rev={rev} up={up} lang={lang} testId={`unit-review-modal-${i}`} />
          ))}
        </div>
      </Modal>
    </section>
  );
};

export default UnitReviews;
